/**
 * Minijuego "El 21 en la canasta" (canon Adri 2026-07-11, ver
 * docs/biblia/minijuegos.md). Mini-baloncesto 1v1 al 21: swipe hacia arriba
 * desde la línea de tiros para lanzar, la bola describe una parábola y
 * encesta si pasa por la boca del aro. Tú vs RIVAL alterno, a 2 puntos
 * por canasta, gana el primero que llegue a 21.
 *
 * Contrato de escena común a los 3 minijuegos (ver instrucciones del
 * orquestador): key EXACTA = id del catálogo, HUD arriba con bringToTop,
 * emite 'minijuego-terminado' al cerrar y sale con this.scene.start si
 * vino con volverA o this.scene.stop si lo lanzó MissionScene.
 */

import Phaser from 'phaser';
import { GameState } from '@/engine/GameState';
import { getMinigame } from '@/data/minijuegos';
import { ensureRunning, SCENE_KEYS } from '@/engine/nav';

// Paleta coherente con MissionScene / PlaceholderScene / HubScene.
const COLOR_FONDO = 0x14141c;
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_CANCHA_LINEA = 0xffffff;
const COLOR_SUELO = 0x3a2a1a;
const COLOR_ARO = 0xff7f24; // naranja
const COLOR_BOLA = 0xe07a1f;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TU = '#f1c40f'; // dorado
const COLOR_RIVAL = '#e74c3c'; // rojo

const HUD_ALTO = 100;
const MARGEN = 24;

/** Geometría de la cancha (vertical). Se calcula en create() según tamaño. */
interface CanchaGeom {
  cx: number;
  ancho: number; // ancho total disponible
  sueloY: number; // y del suelo (los jugadores lanzan desde aquí)
  aroCx: number; // centro del aro
  aroY: number; // y de la boca del aro
  aroRadio: number;
  tableroY: number;
}

interface Bola {
  gfx: Phaser.GameObjects.Arc;
  vx: number;
  vy: number;
  /** si true, ya se contó como canasta/fallo este vuelo. */
  resuelto: boolean;
}

export class MinigameCanastaScene extends Phaser.Scene {
  /** Clave explícita para que el `scene.manager.keys[minijuegoId]` de
   *  MissionScene encuentre esta escena aunque no esté registrada en
   *  nav.ts (lo hace el orquestador). */
  public static readonly KEY = 'mg-21-canasta';

  private volverA: string | null = null;

  private geom!: CanchaGeom;
  private bola!: Bola;

  /** Marcadores. */
  private puntosTu = 0;
  private puntosRival = 0;
  private marcadorTuText!: Phaser.GameObjects.Text;
  private marcadorRivalText!: Phaser.GameObjects.Text;
  private msgText!: Phaser.GameObjects.Text;

  /** Turno: solo se puede lanzar cuando es tu turno y la bola está quieta. */
  private puedeTirar = true;
  private tuTurno = true;
  private terminado = false;

  /** Contenedor del UI del fin de partida. */
  private finContainer: Phaser.GameObjects.Container | null = null;

  /** Punto de inicio del swipe. */
  private swipeInicio: { x: number; y: number } | null = null;
  /** Aro gráfico (lo guardamos para hacer el tween al encestar). */
  private aroGfx!: Phaser.GameObjects.Arc;
  private tableroGfx!: Phaser.GameObjects.Graphics;

  constructor() {
    super(MinigameCanastaScene.KEY);
  }

  init(data?: { volverA?: string }): void {
    this.volverA = data?.volverA ?? null;
    this.puntosTu = 0;
    this.puntosRival = 0;
    this.puedeTirar = true;
    this.tuTurno = true;
    this.terminado = false;
    this.swipeInicio = null;
    this.finContainer = null;
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.construirCancha(width, height);
    this.construirMarcadores();
    this.construirBola();
    this.construirZonaSwipe(width, height);
    this.construirMensaje(width, height);
  }

  // ---------------------------------------------------------------------
  // Construcción del escenario
  // ---------------------------------------------------------------------

  private construirCancha(width: number, height: number): void {
    const cx = width / 2;
    const sueloY = height - 120;
    // Aro arriba con margen bajo el HUD.
    const aroY = HUD_ALTO + 230;
    const aroRadio = 36;
    const aroCx = cx;
    const tableroY = aroY - 22;

    this.geom = { cx, ancho: width, sueloY, aroCx, aroY, aroRadio, tableroY };

    // Suelo.
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_SUELO, 1);
    suelo.fillRect(0, sueloY, width, height - sueloY);
    suelo.lineStyle(2, COLOR_CANCHA_LINEA, 0.4);
    suelo.beginPath();
    suelo.moveTo(0, sueloY);
    suelo.lineTo(width, sueloY);
    suelo.strokePath();

    // Línea de tiros libres (semicírculo abajo, decorativo).
    const tirosLibres = this.add.graphics();
    tirosLibres.lineStyle(2, COLOR_CANCHA_LINEA, 0.5);
    tirosLibres.beginPath();
    tirosLibres.arc(cx, sueloY, 90, Math.PI, 0, true);
    tirosLibres.strokePath();

    // Poste del tablero.
    const poste = this.add.graphics();
    poste.fillStyle(0x666666, 1);
    poste.fillRect(aroCx + 90, tableroY, 4, sueloY - tableroY - 20);

    // Tablero (rect blanco con borde).
    this.tableroGfx = this.add.graphics();
    this.tableroGfx.fillStyle(0xffffff, 1);
    this.tableroGfx.fillRect(aroCx - 70, tableroY - 50, 140, 90);
    this.tableroGfx.lineStyle(2, COLOR_BORDE_PANEL, 1);
    this.tableroGfx.strokeRect(aroCx - 70, tableroY - 50, 140, 90);
    // Cuadradito del tablero (debajo del aro, decorativo).
    this.tableroGfx.lineStyle(3, COLOR_CANCHA_LINEA, 0.7);
    this.tableroGfx.strokeRect(aroCx - 30, tableroY - 5, 60, 40);

    // Aro: elipse + parte trasera del tablero.
    this.aroGfx = this.add.circle(aroCx, aroY, aroRadio, COLOR_ARO);
    this.aroGfx.setStrokeStyle(4, 0xffffff, 1);
    // Línea de "red" (decorativa).
    const red = this.add.graphics();
    red.lineStyle(2, 0xffffff, 0.7);
    for (let i = -3; i <= 3; i++) {
      const x = aroCx + (i * aroRadio) / 3;
      red.beginPath();
      red.moveTo(x, aroY);
      red.lineTo(x + 6, aroY + 30);
      red.strokePath();
    }
  }

  private construirBola(): void {
    const radio = 18;
    const gfx = this.add.circle(this.geom.cx, this.geom.sueloY - 30, radio, COLOR_BOLA);
    gfx.setStrokeStyle(2, 0x000000, 0.8);
    this.bola = { gfx, vx: 0, vy: 0, resuelto: false };
  }

  private construirMarcadores(): void {
    // Nombres + marcadores.
    const yMarcador = HUD_ALTO + 40;
    this.add
      .text(this.geom.cx - 120, yMarcador, 'TÚ', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: COLOR_TU,
      })
      .setOrigin(0.5);
    this.add
      .text(this.geom.cx + 120, yMarcador, 'RIVAL', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: COLOR_RIVAL,
      })
      .setOrigin(0.5);

    this.marcadorTuText = this.add
      .text(this.geom.cx - 120, yMarcador + 36, '0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '38px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5);
    this.marcadorRivalText = this.add
      .text(this.geom.cx + 120, yMarcador + 36, '0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '38px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5);

    // "A 21" pequeño entre los dos.
    this.add
      .text(this.geom.cx, yMarcador + 36, 'al 21', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: COLOR_TEXTO_DIM,
      })
      .setOrigin(0.5);
  }

  private construirMensaje(width: number, height: number): void {
    this.msgText = this.add
      .text(width / 2, height - 60, 'Desliza hacia arriba para tirar', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: COLOR_TEXTO_DIM,
      })
      .setOrigin(0.5);
  }

  /** Zona táctil transparente que cubre toda la parte jugable. */
  private construirZonaSwipe(width: number, height: number): void {
    const zona = this.add
      .zone(width / 2, height / 2, width, height)
      .setInteractive({ useHandCursor: true });
    zona.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.puedeTirar || this.terminado || !this.tuTurno) return;
      this.swipeInicio = { x: pointer.x, y: pointer.y };
    });
    zona.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.puedeTirar || this.terminado || !this.tuTurno) return;
      if (!this.swipeInicio) return;
      const dx = pointer.x - this.swipeInicio.x;
      const dy = pointer.y - this.swipeInicio.y;
      this.swipeInicio = null;
      // Necesitamos un swipe claramente "hacia arriba" (dy negativo grande
      // vs dx) para que no se dispare con un tap perdido.
      if (dy > -40) return;
      this.lanzarBola(dx, dy);
    });
    // Si el dedo sale de la zona sin soltar, descartamos el swipe.
    zona.on('pointerout', () => {
      this.swipeInicio = null;
    });
  }

  // ---------------------------------------------------------------------
  // Tiro y física simple de la bola
  // ---------------------------------------------------------------------

  /** Lanza la bola con vector (dx, dy) en píxeles del swipe. dy es negativo
   *  cuando el dedo va hacia arriba. Cuanto más largo el swipe, más fuerza. */
  private lanzarBola(dx: number, dy: number): void {
    if (this.terminado) return;
    this.puedeTirar = false;

    // Convertimos el vector del swipe en velocidad inicial.
    // Clamp de longitud (swipes exagerados = tope).
    const longitudSwipe = Math.hypot(dx, dy);
    const longitudMax = 320;
    const k = Math.min(longitudSwipe, longitudMax) / longitudMax; // 0..1

    // vx: proporcional al desvío lateral (limitado).
    const vxMax = 380;
    const vyNegativoObjetivo = -720; // hacia arriba (Phaser y crece hacia abajo)
    const vx = Phaser.Math.Clamp((dx / Math.max(longitudSwipe, 1)) * vxMax * k, -vxMax, vxMax);
    const vy = vyNegativoObjetivo * (0.55 + 0.45 * k); // un poco más de fuerza con swipe largo

    this.bola.vx = vx;
    this.bola.vy = vy;
    this.bola.resuelto = false;

    this.msgText.setText('¡Tira!');
  }

  override update(_time: number, delta: number): void {
    if (this.terminado) return;
    if (this.puedeTirar) return;

    const dt = delta / 1000;
    const g = 1400; // px/s²
    const b = this.bola;
    b.gfx.x += b.vx * dt;
    b.gfx.y += b.vy * dt;
    b.vy += g * dt;

    // Detección de canasta: la bola cruza la boca del aro en sentido
    // descendente (vy > 0) y entra dentro del radio del aro. Usamos el
    // momento en que cruza la horizontal aroY.
    if (!b.resuelto && b.vy > 0 && b.gfx.y >= this.geom.aroY) {
      const distAlCentro = Math.abs(b.gfx.x - this.geom.aroCx);
      if (distAlCentro < this.geom.aroRadio - 4) {
        b.resuelto = true;
        this.resolverCanasta(true);
      } else {
        b.resuelto = true;
        this.resolverCanasta(false);
      }
      return;
    }

    // Suelo: si toca el suelo antes que el aro, fallo.
    if (!b.resuelto && b.gfx.y >= this.geom.sueloY - 18) {
      b.resuelto = true;
      this.resolverCanasta(false);
      return;
    }

    // Sale de pantalla por arriba (caso raro de fuerza insuficiente): nada,
    // la bola caerá y la recogeremos abajo.

    // Sale por los lados mucho (rebote simple con las paredes del canvas).
    if (b.gfx.x < 18) {
      b.gfx.x = 18;
      b.vx = Math.abs(b.vx) * 0.6;
    } else if (b.gfx.x > this.scale.width - 18) {
      b.gfx.x = this.scale.width - 18;
      b.vx = -Math.abs(b.vx) * 0.6;
    }
  }

  /** Resuelve el resultado del tiro actual y avanza el flujo del partido. */
  private resolverCanasta(acierto: boolean): void {
    // Pequeña pausa para que el jugador vea el resultado antes de la
    // siguiente ronda.
    this.time.delayedCall(550, () => {
      this.bola.gfx.setPosition(this.geom.cx, this.geom.sueloY - 30);
      this.bola.vx = 0;
      this.bola.vy = 0;

      if (this.tuTurno) {
        if (acierto) {
          this.puntosTu += 2;
          this.marcadorTuText.setText(String(this.puntosTu));
          this.tweenEnceste();
          this.flashMensaje('¡Canasta! +2', COLOR_TU);
        } else {
          this.flashMensaje('Has fallado', COLOR_TEXTO_DIM);
        }
        this.tuTurno = false;

        // Turno del rival: tras una pequeña pausa, "anota" con probabilidad.
        this.time.delayedCall(900, () => this.turnoRival());
      }
      // Si no era tu turno (turno rival) no debería llegarse aquí.
    });
  }

  /** IA invisible del rival: decide si anota (2 pts) y vuelve a tu turno. */
  private turnoRival(): void {
    if (this.terminado) return;
    // Animación de "tiro" del rival: bola sube rápido al aro y cae.
    const tweenSubir = this.tweens.add({
      targets: this.bola.gfx,
      x: this.geom.aroCx + Phaser.Math.Between(-20, 20),
      y: this.geom.aroY - 40,
      duration: 320,
      ease: 'Quad.easeOut',
    });
    tweenSubir.once('complete', () => {
      const anota = Math.random() < 0.45;
      this.time.delayedCall(220, () => {
        if (anota) {
          this.puntosRival += 2;
          this.marcadorRivalText.setText(String(this.puntosRival));
          this.tweenEnceste();
          this.flashMensaje('¡El rival anota!', COLOR_RIVAL);
          // La bola sigue cayendo hacia el suelo.
          this.tweens.add({
            targets: this.bola.gfx,
            y: this.geom.sueloY - 30,
            duration: 260,
            ease: 'Bounce.easeOut',
          });
        } else {
          this.flashMensaje('El rival falla', COLOR_TEXTO_DIM);
          this.tweens.add({
            targets: this.bola.gfx,
            y: this.geom.sueloY - 30,
            duration: 260,
            ease: 'Bounce.easeOut',
          });
        }

        this.time.delayedCall(750, () => {
          if (this.haTerminado()) {
            this.finalizarPartida();
            return;
          }
          this.tuTurno = true;
          this.puedeTirar = true;
          this.msgText.setText('Tu turno — desliza hacia arriba');
        });
      });
    });
  }

  private haTerminado(): boolean {
    return this.puntosTu >= 21 || this.puntosRival >= 21;
  }

  /** Mini-animación del aro al encestar (rebote de color). */
  private tweenEnceste(): void {
    this.aroGfx.setFillStyle(0xffffff, 1);
    this.tweens.add({
      targets: this.aroGfx,
      scaleX: 1.25,
      scaleY: 0.85,
      duration: 90,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.aroGfx.setFillStyle(COLOR_ARO, 1);
      },
    });
    this.tweens.add({
      targets: this.tableroGfx,
      alpha: 0.4,
      duration: 90,
      yoyo: true,
    });
  }

  /** Mensaje central temporal. */
  private flashMensaje(texto: string, color: string): void {
    this.msgText.setText(texto);
    this.msgText.setColor(color);
    this.time.delayedCall(900, () => {
      this.msgText.setColor(COLOR_TEXTO_DIM);
    });
  }

  // ---------------------------------------------------------------------
  // Fin de partida
  // ---------------------------------------------------------------------

  private finalizarPartida(): void {
    if (this.terminado) return;
    this.terminado = true;
    this.puedeTirar = false;

    const ganaste = this.puntosTu >= 21;
    const minijuego = getMinigame(MinigameCanastaScene.KEY);

    // Recompensas (aplicadas ya: la misión solo necesita saber exito).
    if (ganaste) {
      GameState.addRespeto(minijuego.recompensaBase.respeto);
      GameState.addDinero(minijuego.recompensaBase.dinero ?? 0);
    } else {
      // Consolación: 1 de respeto para no salir con las manos vacías.
      GameState.addRespeto(1);
    }
    GameState.registrarPartida(MinigameCanastaScene.KEY);

    this.renderPanelResultado(ganaste, minijuego.nombre);
  }

  private renderPanelResultado(exito: boolean, nombreMinijuego: string): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2 - 20;

    this.finContainer = this.add.container(0, 0);

    const panelAncho = width - MARGEN * 2;
    const panelAlto = 320;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 1);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(cx, cy);
    this.finContainer.add(panel);

    this.finContainer.add(
      this.add
        .text(cx, cy - 110, nombreMinijuego, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO_DIM,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );

    this.finContainer.add(
      this.add
        .text(cx, cy - 60, exito ? '¡GANASTE!' : 'PERDISTE', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
          fontStyle: 'bold',
          color: exito ? COLOR_TU : COLOR_RIVAL,
          align: 'center',
        })
        .setOrigin(0.5),
    );

    this.finContainer.add(
      this.add
        .text(cx, cy - 10, `${this.puntosTu} – ${this.puntosRival}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '38px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
        })
        .setOrigin(0.5),
    );

    // Recompensa textual: '✊ +X · 💶 +Y'.
    const minijuego = getMinigame(MinigameCanastaScene.KEY);
    let recompensaTexto: string;
    if (exito) {
      const partes: string[] = [`✊ +${minijuego.recompensaBase.respeto}`];
      const dinero = minijuego.recompensaBase.dinero ?? 0;
      if (dinero > 0) partes.push(`💶 +${dinero}`);
      recompensaTexto = partes.join(' · ');
    } else {
      recompensaTexto = '✊ +1';
    }
    this.finContainer.add(
      this.add
        .text(cx, cy + 40, recompensaTexto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
        })
        .setOrigin(0.5),
    );

    const anchoBoton = 240;
    const altoBoton = 70;
    const botonY = cy + 110;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);
    this.finContainer.add(boton);

    this.finContainer.add(
      this.add
        .text(cx, botonY, 'SEGUIR', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add
      .zone(cx, botonY, anchoBoton, altoBoton)
      .setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.salirMinijuego(exito));
    this.finContainer.add(zona);
  }

  /** Emite el evento global y sale según volverA. */
  private salirMinijuego(exito: boolean): void {
    // Importante: emitir ANTES de detener la escena para que MissionScene
    // (si está pausada escuchando) reciba el evento.
    this.game.events.emit('minijuego-terminado', { exito });
    if (this.volverA) {
      this.scene.start(this.volverA);
    } else {
      this.scene.stop();
    }
  }
}